import { inject } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET, Router } from '@angular/router';
import { createInjectionToken } from 'ngxtension/create-injection-token';

type SecondaryNavigationOptions = {
	outletName: string;
	commands: unknown[];
	options?: {
		replaceUrl?: boolean;
		queryParams?: Record<string, unknown>;
		state?: Record<string, unknown>;
	};
};

/**
 * Secondary route navigation service.
 * Returns a tuple with an injection token and a provider for that token.
 *
 * To use this service, inject the token  and call the `navigateRouterToSecondaryOutlet` method.
 * The service must be provided somewhere - usually in the component that uses is.
 * Provide the service by calling provideSecondaryNavigation() in the providers array of the component.
 *
 *  @example
 * ```ts
 *
 * providers: [provideSecondaryNavigation()]
 *
 *
 * private navigate = injectSecondaryNavigation();
 *
 * ...
 *
 * this.navigate.navigateRouterToSecondaryOutlet({
 * 		outletName: 'options',
 * 		commands: ['options'],
 * })
 *
 * ```
 */
export const [injectSecondaryNavigation, provideSecondaryNavigation] = createInjectionToken(
	() => {
		const router = inject(Router);
		const route = inject(ActivatedRoute);

		return {
			/**
			 *  Causes the router to navigate to commands in secondary outlet, relative to the current activated route.
			 * @param {string} options.outletName - The name of the secondary outlet.
			 * @param {string[]} options.commands - The commands to navigate to.
			 * @param {NavigationExtras} options.options - The `NavigationExtras` options for the navigation.
			 * @returns A promise from the router navigate method.
			 *
			 *  @example
			 * ```ts
			 *
			 * providers: [provideSecondaryNavigation()]
			 *
			 *
			 * private navigate = injectSecondaryNavigation();
			 *
			 * ...
			 *
			 * this.navigate.navigateRouterToSecondaryOutlet({
			 * 		outletName: 'options',
			 * 		commands: ['options'],
			 * })
			 *
			 * ```
			 *
			 */
			navigateRouterToSecondaryOutlet: ({ outletName, commands, options }: SecondaryNavigationOptions) => {
				return navigateSecondary(router, route, outletName, commands, options);
			},
		};
	},
	{
		isRoot: false,
	},
);

/**
 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
 * @returns
 */
export const navigateRouterToSecondaryOutlet = ({
	router,
	route,
	outletName,
	commands,
	options,
}: {
	router: Router;
	route: ActivatedRoute;
	outletName: string;
	commands: unknown[];
	options?: {
		replaceUrl?: boolean;
		queryParams?: Record<string, unknown>;
		state?: Record<string, unknown>;
	};
}) => {
	return navigateSecondary(router, route, outletName, commands, options);
};

/**
 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
 * @param route
 * @param outletName
 * @param commands
 * @param replaceUrl
 */
export const navigateSecondary = (
	router: Router,
	route: ActivatedRoute,
	outletName: string,
	commands: string | unknown[],
	options?: {
		replaceUrl?: boolean;
		queryParams?: Record<string, unknown>;
		queryParamsHandling?: 'merge' | 'preserve' | '';
		state?: Record<string, unknown>;
	},
) => {
	if (options && options.replaceUrl === undefined) {
		options.replaceUrl = true;
	}

	if (options && options.queryParamsHandling === undefined) {
		options.queryParamsHandling = 'merge';
	}

	options ??= {
		replaceUrl: true,
	};
	const primaryRouteUrl = router.createUrlTree(['.'], { relativeTo: route }).root.children[PRIMARY_OUTLET].toString();

	return router.navigate(
		[primaryRouteUrl, { outlets: { [outletName]: Array.isArray(commands) ? commands : [commands] } }],
		options,
	);
};
